import React from 'react';
import { Users, AlertTriangle, CheckCircle } from 'lucide-react';
import '../../styles/components/AvailabilityIndicator.css';

interface AvailabilityIndicatorProps {
  available: number;
  total: number;
  minRequired?: number;
  showText?: boolean;
  size?: 'sm' | 'md' | 'lg';
}

const AvailabilityIndicator: React.FC<AvailabilityIndicatorProps> = ({
  available,
  total,
  minRequired = 30,
  showText = true,
  size = 'md',
}) => {
  const percentage = (available / total) * 100;
  const occupied = total - available;
  const occupancyPercentage = (occupied / total) * 100;

  // Determine color based on availability
  const getColorClass = (): string => {
    if (percentage > 50) return 'indicator-good';
    if (percentage > 20) return 'indicator-medium';
    return 'indicator-low';
  };

  // Check if minimum requirements are met
  const isMinimumMet = occupied >= minRequired;

  const getStatusIcon = () => {
    if (!isMinimumMet) {
      return <AlertTriangle size={size === 'sm' ? 12 : size === 'lg' ? 20 : 16} />;
    }
    if (percentage < 20) {
      return <AlertTriangle size={size === 'sm' ? 12 : size === 'lg' ? 20 : 16} />;
    }
    return <CheckCircle size={size === 'sm' ? 12 : size === 'lg' ? 20 : 16} />;
  };

  return (
    <div className={`availability-indicator size-${size} ${getColorClass()}`}>
      <div className="indicator-header">
        <div className="indicator-icon-group">
          <Users size={size === 'sm' ? 14 : size === 'lg' ? 24 : 18} />
          {getStatusIcon()}
        </div>
        {showText && (
          <div className="indicator-text">
            <span className="available-count">{available}</span>
            <span className="separator">von</span>
            <span className="total-count">{total}</span>
            <span className="label">Plätzen verfügbar</span>
          </div>
        )}
      </div>

      <div className="indicator-progress">
        <div className="progress-bar">
          <div 
            className="progress-fill occupied"
            style={{ width: `${occupancyPercentage}%` }}
            title={`${occupied} Plätze belegt`}
          />
          {!isMinimumMet && (
            <div 
              className="progress-minimum-line"
              style={{ left: `${(minRequired / total) * 100}%` }}
              title={`Mindestgruppengröße: ${minRequired} Personen`}
            />
          )}
        </div>
      </div>

      {!isMinimumMet && showText && (
        <div className="indicator-warning">
          <AlertTriangle size={14} />
          <span>Noch {minRequired - occupied} Personen bis zur Mindestgruppengröße benötigt</span>
        </div>
      )}

      {percentage < 20 && percentage > 0 && showText && (
        <div className="indicator-low-availability">
          <AlertTriangle size={14} />
          <span>Nur noch wenige Plätze verfügbar!</span>
        </div>
      )}

      {percentage === 0 && showText && (
        <div className="indicator-full">
          <span>Ausgebucht</span>
        </div>
      )}
    </div>
  );
};

export default AvailabilityIndicator;